import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Divider, Typography, CardHeader, CardTitle, CardContent, Export, Table, SelectField } from '@otcmarketsgroup/otcm-ui-lib';
import { ROUTES } from '../../constants/routes';
import { CreateExcelFileName } from '../../utils/excelExport';
import Footnote from '../Footnote';
import styles from './BlueSkyComplianceTable.module.scss';

const FILTER_TABS = {
    BOTH: 'all',
    IN_COMPLIANCE: 'in-compliance',
    OUT_OF_COMPLIANCE: 'out-of-compliance',
};

const DEFAULT_COLUMNS = [
    {
        header: 'Jurisdiction',
        id: 'state',
        width: '20%'
    },
    {
        header: 'In Compliance',
        id: 'status',
        width: '20%'
    },
    {
        header: 'Exemptions*',
        id: 'exemptions',
        renderCell: item => (item.status === 'Y' && item.exemptions?.length > 0) ? item.exemptions.join(', ') : ''
    }
];

const BlueSkyComplianceTable = ({ data, asOfDate, symbol }) => {
    const [selectedTab, setSelectedTab] = useState('all');
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);
    const [dataSet, setDataSet] = useState(null);
    const [mobileView, setMobileView] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const isDownloadAvailable = dataSet && Array.isArray(dataSet) && dataSet.length > 0;

    useEffect(() => {
        setDataSet([...data]);
        setIsLoading(false);
    }, [data]);

    useEffect(() => {
        const handleResize = () => {
            // Check browser width and toggle columns accordingly
            if (window.innerWidth < 768) {
                setMobileView(true);
                setColumns(columns.filter(column => column.id !== 'exemptions'));
            } else {
                setMobileView(false);
                setColumns(DEFAULT_COLUMNS);
            }
        };

        // Initial setup
        handleResize();

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [columns]);

    const tableTabs = [
        {
            label: 'All',
            value: FILTER_TABS.BOTH
        },
        {
            label: 'In Compliance',
            value: FILTER_TABS.IN_COMPLIANCE
        },
        {
            label: 'Out of Compliance',
            value: FILTER_TABS.OUT_OF_COMPLIANCE
        }
    ];

    const handleTabChange = e => {
        const { value } = e.target;

        switch (value) {
            case FILTER_TABS.BOTH:
                setSelectedTab(FILTER_TABS.BOTH);
                setDataSet(data);
                break;

            case FILTER_TABS.IN_COMPLIANCE:
                setSelectedTab(FILTER_TABS.IN_COMPLIANCE);
                setDataSet(data.filter(item => item.status === 'Y'));
                break;

            case FILTER_TABS.OUT_OF_COMPLIANCE:
                setSelectedTab(FILTER_TABS.OUT_OF_COMPLIANCE);
                setDataSet(data.filter(item => item.status === 'N'));
                break;

            default:
                console.warn(`Unhandled tab action: ${value}`);
                break;
        }
    };

    return <div className={styles.container}>
        <>
            <div>
                {!mobileView && <CardHeader gutterBottom={false}>
                    <CardTitle title={'Blue Sky Compliance'} />
                    {isDownloadAvailable && <div className={styles.interactionPane}>
                        <div className={styles.filter}>
                            <SelectField
                                type='secondary'
                                disabled={isLoading}
                                onChange={handleTabChange}
                                options={tableTabs}
                                value={selectedTab}
                            />
                        </div>
                        <div className={styles.excelDownload}>
                            {<Export columns={columns} data={dataSet} fileName={CreateExcelFileName(symbol)} />}
                        </div>
                    </div>}
                </CardHeader>}
                {mobileView && <div>
                    <div className={styles.titleSection}>
                        <Typography gutterBottom variant='h4'>
                            Blue Sky Compliance
                        </Typography>
                    </div>
                    <Divider size='small' color='lightgray' />
                    {isDownloadAvailable && <div className={styles.interactionPaneMobile}>
                        <div>
                            <SelectField
                                type='secondary'
                                disabled={isLoading}
                                onChange={handleTabChange}
                                options={tableTabs}
                                value={selectedTab}
                            />
                        </div>
                        <div>
                            {<Export columns={columns} data={dataSet} fileName={CreateExcelFileName(symbol)} />}
                        </div>
                    </div>}
                </div>}
                <CardContent>
                    <Table isLoading={isLoading} columns={columns} data={dataSet} errorText={undefined} emptyValue='Not available' />
                </CardContent>
                <Footnote>
                    *Exemption/Registration Type: Blue Sky laws govern the offer and sale of securities at the state level. While registration is generally required in each state when a company is offering or selling their securities to residents of that state, many states provide exemptions to registration for secondary trading in the OTC market for companies that provide current disclosure and/or meet certain financial and seasoning requirements. For more information on these secondary trading exemptions, see <Link to={`${ROUTES.blueSkyRulesPath}/alaska`}>Blue Sky Rules</Link>.
                </Footnote>
                <Footnote text={`As of ${asOfDate}`} />
            </div>
        </>
    </div>;
};

BlueSkyComplianceTable.propTypes = {
    data: PropTypes.array,
    asOfDate: PropTypes.string,
    symbol: PropTypes.string
};

export default BlueSkyComplianceTable;
